/**
 * ProfileTab — /settings
 * Account details form plus a session card. Demo page: /profile (account). Template example: NextAdmin
 * `profile/account/page.tsx` ("Account details" form, then the danger-zone rows).
 */
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { z } from 'zod';
import { getMeQueryKey, updateMeMutation } from '../../generated/api/@tanstack/react-query.gen.js';
import type { GetMeResponse } from '../../generated/api/types.gen.js';
import { zUserProfileUpdate } from '../../generated/api/zod.gen.js';
import { Form, FormActions, FormSection, TextFormField, useZodForm } from '../../components/form/index.js';
import { Button } from '../../components/ui/Button.js';
import { SettingList, SettingRow } from '../../components/ui/SettingRow.js';
import { useToast } from '../../components/ui/Toast.js';
import { useLogout } from '../../hooks/useAccountSession.js';

// The API takes username and full name; the email is shown read-only, so it is the only UI-only field.
const profileSchema = zUserProfileUpdate.pick({ username: true, fullName: true }).extend({ email: z.string() });

interface ProfileTabProps {
  user: GetMeResponse['user'];
}

export const ProfileTab: React.FC<ProfileTabProps> = ({ user }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const logout = useLogout();
  const save = useMutation(updateMeMutation());
  const form = useZodForm(profileSchema, {
    defaultValues: { email: user.email, username: user.username ?? '', fullName: user.fullName ?? '' },
  });

  const submit = async ({ username, fullName }: z.output<typeof profileSchema>) => {
    await save.mutateAsync({ body: { username, fullName } });
    await queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    notify('Profile updated.');
  };

  return (
    <div className="space-y-8">
      <Form form={form} onSubmit={submit} guardUnsaved>
        <FormSection title="Account details" description="How you appear to your teammates and in Conflui.">
          <TextFormField name="fullName" label="Full name" placeholder="Your full name" autoComplete="name" />
          <TextFormField name="username" label="Username" placeholder="Choose a username" autoComplete="username" />
          <TextFormField
            name="email"
            label="Email"
            type="email"
            readOnly
            description="Your email signs you in and cannot be changed here."
          />
          <FormActions submitLabel="Save changes" onCancel={() => form.reset()} />
        </FormSection>
      </Form>

      <section>
        <h2 className="mb-6 text-xl leading-7 font-semibold text-heading">Session</h2>
        <SettingList card>
          <SettingRow
            title="Sign out"
            description="End your session on this device. You will need to sign in again."
            icon={<LogOut size={18} />}
          >
            <Button variant="danger" onClick={logout}>
              Sign out
            </Button>
          </SettingRow>
        </SettingList>
      </section>
    </div>
  );
};
